import { FC, useMemo } from 'react';
import {
  CategoryFormDataType,
  CategoryRelationFormDataType,
  getValidPeriodDisplayValueWithFormValueAttached,
  Table,
  TextEllipsis,
} from '@atlas-frontend/sdk';
import { ColumnDef, createColumnHelper } from '@tanstack/react-table';
import { useClassifierFormLabelTranslation } from '../form/useClassifierFormLabelTranslation';
import { CategoryRelationsModal } from './CategoryRelationsModal';
import { Flex } from 'antd';

const columnHelper = createColumnHelper<CategoryRelationFormDataType>();

export interface CategoryRelationsViewModal {
  name: 'categories' | 'categoryGroups' | 'combinedGroups';
  categoryData: CategoryFormDataType;
  onClose: () => void;
}

export const CategoryRelationsViewModal: FC<CategoryRelationsViewModal> = (
  props,
) => {
  const { name, categoryData, onClose } = props;
  const { t: tLabel } = useClassifierFormLabelTranslation();

  const isCombinedGroups = name === 'combinedGroups';

  const columns = useMemo(() => {
    const columns: ColumnDef<CategoryRelationFormDataType, any>[] = [
      columnHelper.accessor('sequenceNumber', {
        header: tLabel(`${name}.categoryRelations.sequenceNumber.label`),
        cell: (props) => <TextEllipsis>{props.renderValue()}</TextEllipsis>,
        meta: {
          width: 100,
          className: 'sticky-left',
          textAlign: 'right',
        },
        filterFn: 'includesString',
        sortingFn: 'basic',
      }),
      columnHelper.accessor(
        (rowData) => `${rowData.subCategory} - ${rowData.subCategoryName}`,
        {
          id: 'subCategory',
          header: tLabel(`${name}.categoryRelations.subCategory.label`),
          cell: (props) => (
            <TextEllipsis>{`${props.row.original.subCategory} - ${props.row.original.subCategoryName}`}</TextEllipsis>
          ),
          meta: {
            width: 180,
          },
        },
      ),
    ];

    if (isCombinedGroups) {
      columns.push(
        columnHelper.accessor('subCategoryType', {
          header: tLabel(`${name}.categoryRelations.subCategoryType.label`),
          cell: (props) => <TextEllipsis>{props.renderValue()}</TextEllipsis>,
          meta: {
            width: 100,
          },
        }),
      );
    }

    columns.push(
      columnHelper.accessor(
        (rowData) =>
          getValidPeriodDisplayValueWithFormValueAttached(rowData.validPeriod),
        {
          id: 'validPeriod',
          header: tLabel(`${name}.categoryRelations.validPeriod.label`),
          cell: (props) => <TextEllipsis>{props.renderValue()}</TextEllipsis>,
          meta: {
            width: 266,
          },
          filterFn: 'validPeriod',
        },
      ),
    );

    return columns;
  }, [name, tLabel, isCombinedGroups]);

  return (
    <CategoryRelationsModal
      name={name}
      categoryData={categoryData}
      onClose={onClose}
    >
      <Flex vertical gap="middle" align="end">
        <Table
          name="categoryRelations"
          data={categoryData.categoryRelations}
          columns={columns}
          sticky
          virtual
          scrollContainerSelector="#category-relations-modal-content"
        />
      </Flex>
    </CategoryRelationsModal>
  );
};
